
import Vue, { ref, defineComponent, computed, PropType, onUnmounted, watch } from 'vue'
import { LocationFilters, BBTreeviewNode, NestedFilter, DateNode } from '../types'
import { useFilters } from '../composables/useFilters'
import { useSearchFilters } from '../composables/useSearchFilters'

export default defineComponent({
  name: 'Filters',
  props: {
    locationFilters: {
      type: Array as PropType<Array<LocationFilters>>,
      default: () => []
    },
    searchPhrase: String
  },
  setup(props) {
    const filtersApi = useFilters()
    const { filterTreeView } = useSearchFilters()
    const taxonomySelections = ref([])
    // Initiate treeview (taxonomies) state from local storage
    try {
      const taxonomyFilters = localStorage.getItem('taxonomy-selections')
      if (taxonomyFilters) {
        taxonomySelections.value = JSON.parse(taxonomyFilters)
      }
    } catch (err) {
      console.warn('Error reading taxonomy-selections from local storage ', err)
    }
    const treeview = ref<any>(null)
    const refresh = ref(0)
    const panels = ref<number[]>([])
    const innerPanels = ref<number[]>([])
    const customFieldOuterPanels = ref<number[][]>([])
    const noSearchResultsExist = ref(false)

    watch(
      () => props.locationFilters,
      newFilters => openPanels(newFilters, !!props.searchPhrase?.length)
    )

    const someSelected = computed(() => {
      refresh.value
      return (nodes: Array<BBTreeviewNode>) => {
        if (nodes?.length) return filtersApi.someSelected(nodes)
        return false
      }
    })

    watch(taxonomySelections, () => {
      localStorage.setItem('taxonomy-selections', JSON.stringify(taxonomySelections.value))
    })

    const allSelected = computed(() => {
      refresh.value
      return (nodes: Array<BBTreeviewNode>) => {
        if (nodes?.length) return filtersApi.allSelected(nodes)
        return false
      }
    })

    function setSelected(nodes: Array<BBTreeviewNode>, selection: boolean) {
      filtersApi.setSelected(nodes, selection)
      filtersApi.customFieldsToFilter(nodes, filtersApi.model.value?.custom_fields || [])
      refresh.value += 1
    }

    function updateTreeviewNodes(openAll: boolean) {
      if (treeview.value) Vue.nextTick(() => treeview.value[0]?.updateAll(openAll))
    }

    function openPanels(filters: LocationFilters[], openAll: boolean) {
      // Reset panels and tree view
      updateTreeviewNodes(openAll)
      panels.value = []
      innerPanels.value = []
      let tempCustomFieldOuterPanels: number[][] = []

      panels.value = filters?.map((filter, i) => {
        const nestedFilter = filter as NestedFilter
        if (nestedFilter.panels) {
          innerPanels.value = nestedFilter.panels
            ?.filter(panel => !!panel.children?.length)
            .map((panel, j) => {
              if (openAll) {
                tempCustomFieldOuterPanels[j] = panel.children.map((_, k) => k)
              }

              return j
            })
        }

        return i
      })
      Vue.nextTick(() => {
        customFieldOuterPanels.value = tempCustomFieldOuterPanels
      })
      if (!panels.value.length) {
        noSearchResultsExist.value = true
      } else {
        noSearchResultsExist.value = false
      }
    }

    function selectDate(option: DateNode) {
      filtersApi.upsertCustomFieldDate(option, filtersApi.model.value.custom_fields || [])
    }

    onUnmounted(() => {
      filtersApi.clear()
    })

    function clearTreeViewSelections() {
      taxonomySelections.value = []
      openPanels(props.locationFilters, false)
    }
    const searchingTaxonomies = computed(() => {
      if (!props.searchPhrase?.length) return true
      const taxonomies = ['taxonomy', 'brand', 'division', 'category', 'season', 'year']
      return taxonomies.join('').includes(props.searchPhrase.toLowerCase())
    })
    return {
      taxonomySelections,
      panels,
      treeview,
      selectDate,
      filtersApi,
      innerPanels,
      setSelected,
      allSelected,
      someSelected,
      filterTreeView,
      customFieldOuterPanels,
      clearTreeViewSelections,
      searchingTaxonomies,
      noSearchResultsExist
    }
  }
})
